import {
  getBillingItems,
  createBillingItem,
  updateBillingItem,
  deleteBillingItem,
} from '../../api/admin';
import invalidationManager from './InvalidationManager';
import { BillingItem } from '../../model/BillingItem';

const { hook: useBillingItemsQuery } = invalidationManager.createFetcher({
  queryKey: (companyId: string) => ['company', companyId, 'billingItems'],
  fetchFunction: async (companyId: string): Promise<BillingItem[]> => {
    return getBillingItems(companyId);
  },
});

const { hook: useBillingItem } = invalidationManager.createFetcher({
  queryKey: (companyId: string, billingItemId?: string) => [
    'company',
    companyId,
    'billingItems',
    billingItemId || '',
  ],
  fetchFunction: async (companyId: string, billingItemId?: string): Promise<BillingItem | null> => {
    if (!billingItemId) {
      return null;
    }
    // For now, we'll fetch all billing items and find the one we need
    // TODO: Add individual billing item API endpoint
    const allItems = await getBillingItems(companyId);
    return allItems.find((item) => item.id === billingItemId) || null;
  },
});

const useCreateBillingItemMutation = invalidationManager.createMutationHook({
  mutationFunction: async (
    companyId: string,
    billingItem: Omit<BillingItem, 'createdAt' | 'id' | 'createdBy' | 'discount' | 'taxRate'>,
  ) => {
    await createBillingItem(companyId, billingItem);
    await invalidationManager.invalidate(['company', companyId, 'billingItems']);
    await invalidationManager.invalidate(['company', companyId, 'subscriptions']);
  },
});

const useUpdateBillingItemMutation = invalidationManager.createMutationHook({
  mutationFunction: async (
    companyId: string,
    billingItemId: string,
    billingItem: Partial<BillingItem>,
  ) => {
    await updateBillingItem(companyId, billingItemId, billingItem);
    await invalidationManager.invalidate(['company', companyId, 'billingItems']);
    await invalidationManager.invalidate(['company', companyId, 'billingItems', billingItemId]);
    await invalidationManager.invalidate(['company', companyId, 'subscriptions']);
  },
});

const useDeleteBillingItemMutation = invalidationManager.createMutationHook({
  mutationFunction: async (companyId: string, billingItemId: string) => {
    await deleteBillingItem(companyId, billingItemId);
    await invalidationManager.invalidate(['company', companyId, 'billingItems']);
    await invalidationManager.invalidate(['company', companyId, 'billingItems', billingItemId]);
  },
});

export {
  useBillingItemsQuery,
  useBillingItem,
  useCreateBillingItemMutation,
  useUpdateBillingItemMutation,
  useDeleteBillingItemMutation,
};
